import machineActions from 'js/redux/reducers/Machine';
import errorActions from 'js/redux/reducers/Errors';
import JSONRPC from './JSONRPC';
import type { AxiosProgressEvent } from 'axios';
import { authenticated } from './WebRequest';
import { omitNil } from 'js/mylib/Utils';
import { ERROR_TINTING_MACHINE } from 'js/Constants';
import JSONRPCAPI from './JSONRPCAPI';
import type { JSONRPCQtObject } from './types/JSONRPCAPI';
import type {
  ApiThunk,
  ClientDataRecord,
  ClientDataIdParameters,
  ClientDataParameters,
  DispenserId,
  DispatchStore,
  JsonObject,
  MachineBackupParameters,
  MachineCanister,
  MachineCanisterBatch,
  MachineCommandResponse,
  MachineControllerConfiguration,
  MachineDBColorant,
  MachineDBParameters,
  MachineDBRecord,
  MachineDriverConfiguration,
  MachineError,
  MachineExceptionPayload,
  MachineFormulaItem,
  MachineListItem,
  MachineQtObject,
  MachineRefill,
  MachineRefillRecord,
  MachineRpcResponse,
  MachineState,
  MachineTintOrder,
  Nullable,
  SiteDataParameters,
  MachineBackupFiles,
} from './types/Machine';

export type {
  ApiThunk,
  ClientDataRecord,
  ClientDataIdParameters,
  ClientDataParameters,
  DispenserId,
  DispatchStore,
  JsonObject,
  MachineBackupParameters,
  MachineCanister,
  MachineCanisterBatch,
  MachineCanisterRefill,
  MachineCommandResponse,
  MachineControllerConfiguration,
  MachineDBColorant,
  MachineDBParameters,
  MachineDBRecord,
  MachineDriverConfiguration,
  MachineError,
  MachineExceptionPayload,
  MachineFormulaItem,
  MachineInfo,
  MachineListItem,
  MachineQtObject,
  MachineRefill,
  MachineRefillRecord,
  MachineRpcError,
  MachineRpcResponse,
  MachineState,
  MachineStateEnvelope,
  MachineTintOrder,
  Nullable,
  SiteDataParameters,
} from './types/Machine';

export class MachineAPI extends JSONRPCAPI {
  private dispenser: Nullable<unknown>;

  constructor() {
    super();
    // dummy dispatcher that resolves all calls to null
    this.dispenser = null;
  }

  connect(qtobject: Nullable<JSONRPCQtObject>, store?: DispatchStore): void {
    // connecting all machine actions here
    try {
      const machineQtObject = qtobject as Nullable<MachineQtObject>;
      if (machineQtObject?.machineState && store) {
        machineQtObject.machineState.connect((machinestate: string) => {
          store.dispatch(machineActions.setMachineState(machinestate));
        });

        this.jsonrpc = new JSONRPC((message) => {
          machineQtObject.dispatch(JSON.stringify(message));
        });

        machineQtObject.response.connect((str: string) =>
          this.jsonrpc.handleResponse(JSON.parse(str))
        );

        try {
          machineQtObject.machineException?.connect((str: string) => {
            const error = JSON.parse(str) as MachineExceptionPayload;

            store.dispatch(
              errorActions.showCriticalError(
                error.message,
                error,
                ERROR_TINTING_MACHINE
              )
            );
          });
        } catch (e) {
          // The previous version of POS app doesn't export machineException
        }

        // Assume application loaded so fire the purge action
        this.jsonrpc.request('purge_at_program_startup', []);
      }
    } catch (e) {
      console.log('Error in Machine Qt connection');
      console.log(e);
    }
  }

  // Notes:
  // - All methods return promises
  // - dispID must be the first parameter if any parameters are sent; null can be used as placeholder

  machines_list(): Promise<MachineRpcResponse<MachineListItem[]>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineListItem[]>>(
      'machines_list',
      []
    );
  }

  moveToFill(
    dispID: DispenserId,
    canid: number
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('move_to_fill', [
      dispID,
      canid,
    ]);
  }

  moveToHome(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('move_to_home', [
      dispID,
    ]);
  }

  getColorants(
    dispID: DispenserId,
    getBatches = false
  ): Promise<MachineRpcResponse<MachineCanister[]>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineCanister[]>>(
      'canisters_list',
      [dispID, getBatches]
    );
  }

  getConfiguration(
    dispID: DispenserId
  ): Promise<MachineRpcResponse<MachineControllerConfiguration>> {
    return this.jsonrpc.request<
      MachineRpcResponse<MachineControllerConfiguration>
    >('controller_configuration', [dispID]);
  }

  getDriverConfiguration(): Promise<
    MachineRpcResponse<MachineDriverConfiguration>
  > {
    return this.jsonrpc.request<MachineRpcResponse<MachineDriverConfiguration>>(
      'configuration',
      []
    );
  }

  getSupportedCommands(
    dispID: DispenserId
  ): Promise<MachineRpcResponse<string[]>> {
    return this.jsonrpc.request<MachineRpcResponse<string[]>>(
      'supported_commands',
      [dispID]
    );
  }

  getErrors(dispID: DispenserId): Promise<MachineRpcResponse<MachineError[]>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineError[]>>(
      'errors_list',
      [dispID]
    );
  }

  initialize(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('initialize', [
      dispID,
    ]);
  }

  reset(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('reset', [dispID]);
  }

  cap(
    dispID: DispenserId,
    value: number
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('cap', [
      dispID,
      value,
    ]);
  }

  resetHard(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('reset_hard', [
      dispID,
    ]);
  }

  clean(
    dispID: DispenserId,
    circuitid: number
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('clean', [
      dispID,
      circuitid,
    ]);
  }

  cleanAll(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('clean_all', [
      dispID,
    ]);
  }

  clearErrors(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('errors_clean', [
      dispID,
    ]);
  }

  setConfiguration(
    dispID: DispenserId,
    config: MachineControllerConfiguration
  ): Promise<MachineRpcResponse<MachineCommandResponse>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineCommandResponse>>(
      'controller_configuration_set',
      [dispID, config, true, true]
    );
  }

  setDriverConfiguration(
    config: MachineDriverConfiguration
  ): Promise<MachineRpcResponse<MachineCommandResponse>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineCommandResponse>>(
      'configuration_set',
      [config]
    );
  }

  setColorants(
    dispID: DispenserId,
    levels: MachineCanister[]
  ): Promise<MachineRpcResponse<MachineCommandResponse>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineCommandResponse>>(
      'levels_set',
      [dispID, convertDatesInLevels(levels)]
    );
  }

  setColorantsFromDb(
    cnts: MachineDBColorant[]
  ): Promise<MachineRpcResponse<MachineCommandResponse>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineCommandResponse>>(
      'colorants_from_db_set',
      [cnts]
    );
  }

  getState(dispID: DispenserId): Promise<MachineRpcResponse<MachineState>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineState>>(
      'machine_status',
      [dispID]
    );
  }

  getRefillsList(
    dispID: DispenserId,
    canisterIndex?: Nullable<number>,
    startDate?: Nullable<string>,
    endDate?: Nullable<string>,
    startingFrom?: Nullable<number>,
    maxResults?: Nullable<number>
  ): Promise<MachineRpcResponse<MachineRefill[]>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineRefill[]>>(
      'refills_list',
      [dispID, canisterIndex, startDate, endDate, startingFrom, maxResults]
    );
    /**
     * Gets list of all refills

    :param canisterIndex: if present, result contains only canister that equal to this param
    :type canisterIndex: integer

    :param startDate: get orders with timestamp great than
    :type startDate: datetime

    :param endDate: get orders with timestamp less then
    :type endDate: datetime

    :param startingFrom: if present, result contains only items with rowid higher or equal to this param
    :type startingFrom: int

    :param maxResults: the number of results wanted
    :type maxResults: int

    :return: a list of dictionaries
    */
  }

  addRefill(
    dispID: DispenserId,
    canisterIndex: number,
    volume: number,
    batch?: Nullable<string>,
    refillDate?: Nullable<string>,
    user?: Nullable<string>,
    note?: Nullable<string>
  ): Promise<MachineRpcResponse<MachineCommandResponse>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineCommandResponse>>(
      'refill_add',
      [
        dispID, // Required
        canisterIndex, // Required
        volume, // Required
        batch,
        refillDate,
        user,
        note,
      ]
    );

    /**
     * Documentation from chromaflo driver of this function

        :param canisterIndex: index of canister that was refilled
        :type canisterIndex: integer

        :param volume: volume, that was added to canister
        :type volume: unicode

        :param batch: batch number
        :type batch: unicode

        :param refillDate: datetime of refill
        :type refillDate: datetime

        :param user: user
        :type user: unicode

        :param note: note
        :type note: unicode

        :return: None
     */
  }

  addBatchRefill(
    dispID: DispenserId,
    canisterIndex: number,
    delta: number,
    currLevel: number,
    maxLevel: number,
    batch: string,
    refillDate?: Nullable<string>,
    expirationDate?: Nullable<string>,
    minVolume?: Nullable<number>
  ): Promise<MachineRpcResponse<MachineCommandResponse>> {
    /**
        Updates refill for particular canister


        :param canisterIndex: if present, result contains only canister that equal to this param
        :type canisterIndex: integer

        :param delta: canister change value (can be also negative)
        :type delta: double

        :param currLevel: current level of canister (delta is already included)
        :param currLevel: double

        :param maxLevel: maximum level of canister
        :param maxLevel: double

        :param batch: batch number
        :type batch: unicode

        :param refillDate: datetime of refill
        :type refillDate: datetime

        :param expirationDate: datetime when batch will expire
        :type expirationDate: datetime

        :param minVolume: minimum volume when the batch will no longer taken into consideration
        :type user: unicode

        :return: None
        */
    return this.jsonrpc.request<MachineRpcResponse<MachineCommandResponse>>(
      'batch_refill_add',
      [
        dispID, // Required
        canisterIndex, // Required
        delta,
        currLevel,
        maxLevel,
        batch,
        refillDate,
        expirationDate,
        minVolume,
      ]
    );
  }

  getBatchRefillsList(
    dispID: DispenserId,
    canisterIndex?: Nullable<number>,
    currLevel?: Nullable<number>
  ): Promise<MachineRpcResponse<MachineCanisterBatch[]>> {
    /**
     * Gets list of all refills

        :param canisterIndex: if present, result contains only canister that equal to this param
        :type canisterIndex: integer

        :param currLevel: current level of canister (delta is already included)
        :param currLevel: double

        :return: a list of dictionaries
     */
    return this.jsonrpc.request<MachineRpcResponse<MachineCanisterBatch[]>>(
      'batch_refills_list',
      [
        dispID, // Required
        canisterIndex, // Required
        currLevel,
      ]
    );
  }

  stir(
    dispID: DispenserId,
    canid: number,
    time: number
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('stir', [
      dispID,
      [{ id: canid, time }],
    ]);
  }

  stirAll(
    dispID: DispenserId,
    time: number
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('stir_all', [
      dispID,
      time,
    ]);
  }

  clearDispenserWarning(
    dispID: DispenserId
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>(
      'clear_dispenser_warning',
      [dispID]
    );
  }

  clearHopperWarning(
    dispID: DispenserId
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>(
      'clear_hopper_warning',
      [dispID]
    );
  }

  purgeAll(
    dispID: DispenserId,
    amount: number,
    sequential: boolean
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('purge_all', [
      dispID,
      amount,
      sequential,
    ]);
  }

  purgeAutomaticPrepare(
    dispID: DispenserId
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>(
      'purge_automatic_prepare',
      [dispID]
    );
  }

  purgeAutomatic(dispID: DispenserId): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>(
      'purge_automatic',
      [dispID]
    );
  }

  backup(
    dispID: DispenserId,
    maxsize: number
  ): Promise<MachineRpcResponse<MachineBackupFiles>> {
    return this.jsonrpc.request<MachineRpcResponse<MachineBackupFiles>>(
      'backup',
      [dispID, maxsize]
    );
  }

  purge(
    dispID: DispenserId,
    frm: MachineFormulaItem[],
    sequential: boolean
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('purge', [
      dispID,
      frm,
      sequential,
    ]);
  }

  machine_action(
    dispID: DispenserId,
    machine_action: string
  ): Promise<MachineRpcResponse<boolean>> {
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('machine_action', [
      dispID,
      machine_action,
    ]);
  }

  check_formula(
    dispID: DispenserId,
    frm: MachineFormulaItem[]
  ): Promise<MachineRpcResponse<MachineFormulaItem[]>> {
    /**
     * @frm:[{'code': 'FT', 'volume': 12.091},
     *       {'code': 'LT', 'volume': 5.89}, ..]
     */
    return this.jsonrpc.request<MachineRpcResponse<MachineFormulaItem[]>>(
      'check_formula',
      [dispID, frm]
    );
  }

  validate_formula(
    dispID: DispenserId,
    frm: MachineFormulaItem[]
  ): Promise<MachineRpcResponse<number>> {
    /**
     * @frm:[{'code': 'FT', 'volume': 12.091},
     *       {'code': 'LT', 'volume': 5.89}, ..]
     */
    return this.jsonrpc.request<MachineRpcResponse<number>>(
      'validate_formula',
      [dispID, frm]
    );
  }

  /**
   * Send tinting command to machine
   * NOTE: check driver_config for supportsConfirmEarly before enabling confirmEarly parameter
   */
  tint(
    dispID: DispenserId,
    order: MachineTintOrder,
    barcodeValidation = false,
    confirmEarly = false
  ): Promise<MachineRpcResponse<boolean>> {
    /**
     * @order: {'productName': 'Test product',
                'cansTinted': 0,
                'lotSize': 3,
                'formula': {'base': 'B',
                            'cnts': [{'code': 'FT', 'volume': 12.091},
                                      {'code': 'LT', 'volume': 5.89},
                                      {'code': 'VT', 'volume': 8.71}]},
                'can': {'name': '1 l',
                        'punch': 1,
                        'shelfPositions': [20, 0, -1]}}
     */
    if (confirmEarly) {
      return this.jsonrpc.request<MachineRpcResponse<boolean>>('tint', [
        dispID,
        order,
        false, // sequential = false,
        barcodeValidation,
        false, // setTintingInfo = false
        true, // confirmEarly = true
      ]);
    }
    // Compatibility with old versions of Driver that do not expect 6th parameter
    return this.jsonrpc.request<MachineRpcResponse<boolean>>('tint', [
      dispID,
      order,
      false, // sequential = false,
      barcodeValidation,
    ]);
  }

  /*
  get_machine_DB_id(clientid, machineinfo) {
    /**
     * Getting machine database id
     * Deprecated use get_machine_DB instead!
    return authenticated.get(`/query/clientid/${clientid}/machineid/`, {
      params: machineinfo,
    });
  }
  
   */

  get_machine_DB(parameters: MachineDBParameters): ApiThunk<MachineDBRecord> {
    /**
     * Getting machine from database
     */

    const { siteid, clientid, ...params } = omitNil(parameters);
    return authenticated.post(
      `/query/site/${siteid}/client/${clientid}/machine`,
      { params }
    );
  }

  set_refills_list(data: JsonObject): ApiThunk<MachineRefillRecord> {
    return authenticated.post('/rest/refill/', data);
  }

  connected_machines_DB(parameters: MachineDBParameters): ApiThunk<string> {
    /**
     * Sending info to backend which machines are actually connected to driver
     */

    const { siteid, clientid, ...params } = omitNil(parameters);
    return authenticated.post(
      `/query/site/${siteid}/client/${clientid}/connected_machines`,
      { params }
    );
  }

  save_client_data(parameters: SiteDataParameters): ApiThunk<ClientDataRecord> {
    const { siteid, ...params } = omitNil(parameters);
    return authenticated.post(`/rest/site/${siteid}/clientdata/`, params);
  }

  save_backup(parameters: MachineBackupParameters): ApiThunk<string> {
    const { siteid, dataid, data } = parameters;
    // Send data as base64 encoded
    return authenticated.put(
      `/rest/site/${siteid}/clientdata/${dataid}/data`,
      new Blob([data]),
      {
        headers: {
          'Content-Type': 'application/zip',
          'Content-Encoding': 'base64',
        },
      }
    );
  }

  fetch_client_data(
    parameters: ClientDataParameters
  ): ApiThunk<ClientDataRecord[]> {
    const { siteid, clientid } = omitNil(parameters);
    return authenticated.get(
      `/rest/site/${siteid}/clientdata/?clientid=${clientid}`
    );
  }

  delete_client_data(parameters: ClientDataIdParameters): ApiThunk<string> {
    const { siteid, dataid } = omitNil(parameters);
    return authenticated.delete(`/rest/site/${siteid}/clientdata/${dataid}`);
  }
}

export function download_client_data(
  parameters: ClientDataIdParameters,
  callback?: (progressEvent: AxiosProgressEvent) => void
): ApiThunk<Blob> {
  const { siteid, dataid } = omitNil(parameters);
  return authenticated.get(`/rest/site/${siteid}/clientdata/${dataid}/data`, {
    responseType: 'blob',
    onDownloadProgress: callback,
  });
}

/**
 * Date format conversion needed by levels_set
 * @param {array} levels
 * @returns array
 */
function convertDatesInLevels(levels: MachineCanister[]): MachineCanister[] {
  return levels.map((canister) => ({
    ...canister,
    refillCans: (canister.refillCans || []).map((refill) => {
      const { date } = refill;
      return {
        ...refill,
        date: date ? date.replace('T', ' ').replace('Z', '') : null,
      };
    }),
  }));
}

const api = new MachineAPI();

export { api as default };
